/**
 * Hook to restore authentication state from localStorage on app startup
 */

import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { POST_USER_DATA } from "../store/action";
import { getStoredUserData, hasStoredAuth } from "../utils/authStorageUtils";
import { createLogger } from "../utils/logger";

const logger = createLogger("AuthRestore");

export const useAuthRestore = () => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user);

  useEffect(() => {
    logger.log("🚀 AuthRestore hook starting...", {
      hasUserDataInRedux: !!userData,
      userIdInRedux: userData?._id,
      hasRefreshTokenInRedux: !!userData?.refresh_token
    });

    // Only restore if user is not already logged in Redux
    if (!userData?._id) {
      logger.log("🔄 No user in Redux, checking for stored authentication...");
      
      // Check localStorage directly
      const hasStored = hasStoredAuth();
      logger.log("📦 Storage check result:", { hasStored });
      
      if (hasStored) {
        const storedUserData = getStoredUserData();
        logger.log("📦 Retrieved stored data:", {
          hasStoredData: !!storedUserData,
          storedUserId: storedUserData?._id,
          storedEmail: storedUserData?.email,
          hasStoredRefreshToken: !!storedUserData?.refresh_token,
          storedRefreshTokenLength: storedUserData?.refresh_token?.length,
          storedKeys: storedUserData ? Object.keys(storedUserData) : []
        });
        
        if (storedUserData && storedUserData._id && storedUserData.refresh_token) {
          logger.log("✅ Restoring user session from localStorage to Redux");
          
          // Dispatch to Redux to restore session
          dispatch({
            type: POST_USER_DATA,
            data: storedUserData
          });
        } else {
          logger.warn("⚠️ Stored auth data is invalid or incomplete:", {
            hasId: !!storedUserData?._id,
            hasToken: !!storedUserData?.refresh_token
          });
        }
      } else {
        logger.log("ℹ️ No stored authentication found");
      }
    } else {
      logger.log("ℹ️ User already authenticated in Redux, skipping restore");
    }
  }, []); // Only run once on app startup

  return null; // This hook doesn't return anything, just performs side effects
};